import { CircleCheckIcon, UsersIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

import type {
  ForgeAccountView,
  ForgeHostView,
} from '@/lib/api/generated/model';
import { displayNameOf, initialsOf } from '@/shared/components/accountIdentity';

import { PREVIEW_COPY, PREVIEW_LINK_COPY } from '../copy';
import {
  forgeName,
  hostUsage,
  PLATFORM_COMMITTER,
  pushedAs,
} from '../forgeAccounts';
import { ForgeMark } from './ForgeMark';

/**
 * The next commit as the forges will show it (spec 163 US1): who authored it,
 * who committed it, the run it links back to, and whose name the push and the
 * merge request carry on each host. Illustrative, not a real commit: it is the
 * answer to "will my work show up as me?".
 */
export function CommitPreview({
  user,
  authorEmail,
  emailSource,
  hosts,
  accounts,
  workspaceNames,
  justLinkedHost,
  onLink,
}: {
  user: { display_name: string; email: string };
  authorEmail: string;
  /** Where the author email comes from, in a few words. */
  emailSource: string;
  hosts: readonly ForgeHostView[];
  accounts: readonly ForgeAccountView[];
  workspaceNames: ReadonlyMap<string, string>;
  /** A host the person has just linked by signing in: its line arrives. */
  justLinkedHost: string | null;
  /** Go to a host's account card to link it (spec 170). */
  onLink?: (host: string) => void;
}) {
  const name = displayNameOf(user.display_name, user.email);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="bg-muted text-foreground grid size-10 shrink-0 place-items-center rounded-full text-sm font-semibold"
        >
          {initialsOf(user.display_name, user.email)}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-foreground text-sm">
            <span className="font-medium">{PREVIEW_COPY.authored(name)}</span>
            <span className="text-muted-foreground">
              {' · '}
              {PREVIEW_COPY.committed(PLATFORM_COMMITTER)}
            </span>
          </p>
          <p className="text-muted-foreground text-xs [overflow-wrap:anywhere]">
            <span className="font-mono">{authorEmail}</span>
            {' · '}
            <span>{emailSource}</span>
          </p>
        </div>
      </div>

      {/* Wraps rather than scrolling sideways: on a phone the line ran off the
          card mid-sentence, and nothing said it scrolled (spec 184). */}
      <p className="bg-muted text-muted-foreground rounded-md px-2.5 py-1.5 font-mono text-xs [overflow-wrap:anywhere] whitespace-pre-wrap">
        {PREVIEW_COPY.trailer}
      </p>

      {hosts.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {hosts.map((host) => (
            <PushedLine
              key={host.host}
              host={host}
              account={accounts.find((a) => a.host === host.host) ?? null}
              workspaceNames={workspaceNames}
              arriving={host.host === justLinkedHost}
              onLink={onLink}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function PushedLine({
  host,
  account,
  workspaceNames,
  arriving,
  onLink,
}: {
  host: ForgeHostView;
  account: ForgeAccountView | null;
  workspaceNames: ReadonlyMap<string, string>;
  arriving: boolean;
  onLink?: (host: string) => void;
}) {
  const pushed = pushedAs(account);
  const personal = pushed.kind === 'personal';
  const usage = hostUsage(host, workspaceNames);
  const stopped =
    !personal && (usage.required.length > 0 || usage.requiredUnnamed > 0);
  const StateIcon = personal ? CircleCheckIcon : UsersIcon;

  return (
    <li
      className={`flex items-start gap-2.5 rounded-md px-2.5 py-2 text-sm ${
        personal ? 'bg-state-success/10' : 'bg-muted'
      } ${
        arriving
          ? 'animate-in fade-in-0 slide-in-from-bottom-1 duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] motion-reduce:animate-none'
          : ''
      }`}
    >
      <ForgeMark
        provider={host.provider}
        className="text-muted-foreground mt-0.5 size-4 shrink-0"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground [overflow-wrap:anywhere]">
          <span className="font-medium">{host.host}</span>
          {': '}
          {pushed.kind === 'personal'
            ? PREVIEW_COPY.pushedYou(pushed.username)
            : PREVIEW_COPY.pushedShared}
        </span>
        {pushed.kind === 'shared' ? (
          <span className="text-muted-foreground text-xs">
            {pushed.reason === 'not-linked'
              ? PREVIEW_COPY.notLinked(forgeName(host.provider))
              : PREVIEW_COPY.relink}
            {stopped ? (
              <span className="text-state-warning">
                {' '}
                {PREVIEW_COPY.requiredStops(
                  usage.required.length > 0
                    ? usage.required.join(', ')
                    : PREVIEW_COPY.workspaces(usage.requiredUnnamed),
                )}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      {pushed.kind === 'shared' && onLink ? (
        // Linking from the line that shows the gap (spec 170), so it is never
        // below the fold: it brings the host's card into view, focused.
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          aria-label={
            pushed.reason === 'not-linked'
              ? PREVIEW_LINK_COPY.linkLabel(forgeName(host.provider), host.host)
              : PREVIEW_LINK_COPY.relinkLabel(
                  forgeName(host.provider),
                  host.host,
                )
          }
          onClick={() => onLink(host.host)}
        >
          {pushed.reason === 'not-linked'
            ? PREVIEW_LINK_COPY.link
            : PREVIEW_LINK_COPY.relink}
        </Button>
      ) : (
        <StateIcon
          aria-hidden="true"
          className={`mt-0.5 size-4 shrink-0 ${personal ? 'text-state-success' : 'text-muted-foreground'}`}
        />
      )}
    </li>
  );
}
